/* D-87 · the welcome page in miniature — one renderer for the three layout thumbnails and the
   phone preview of step 2 (spec welcome-layouts-v1, accepted 07/10). It draws the merchant's own
   page from the D-86 fields: the logo and the colour (Marque), the campaign's photo and its one
   sentence (« Titre d'entrée »). Nothing else is themeable.

   It is laid out once at 300 × 620 and scaled, so a thumbnail is a true miniature of the preview,
   not an icon. Colours on the band are never the merchant's choice: the text takes ink or white,
   whichever reads better on their colour, and the button wears their colour only when it holds
   4.5:1 on paper — otherwise it stays leaf and the colour lives on the band.
   A campaign with no gift (« Rien — la demande seule ») draws the player's no-gift entry: no roue on the page,
   « Commencer », and no gift assurance line (parcours-marchand-v2, custWelcome; apps/player/d.html). */
import type { CSSProperties } from 'react'
import { Wheel, type WheelDesign } from '@/lib/wheel'
import type { WelcomeLayout } from '@/lib/campaign'

const PAPER = '#FAF8F2'
const INK = '#20261F'
const LEAF = '#237A55'
const W = 300, H = 620

function lum(hex: string): number {
  const n = parseInt(hex.replace('#', '').slice(0, 6), 16)
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4) })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}
export const contrast = (a: string, b: string) => { const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p); return (x + 0.05) / (y + 0.05) }
/** Ink or white on the merchant's colour — whichever holds the higher contrast ratio, never a luminance threshold,
    never the merchant's pick; a tie goes to ink, exactly as the player's `--on-brand` (d87_flows, theme()). */
export const textOn = (hex: string) => (contrast(hex, INK) >= contrast(hex, '#FFFFFF') ? INK : '#FFFFFF')
/** The primary button: the brand colour when it holds 4.5:1 on paper, else leaf. */
export const buttonOf = (hex: string | null | undefined) => (hex && contrast(hex, PAPER) >= 4.5 ? hex : LEAF)

/** The mock photo: a warm interior in gradients (the prototype's stand-in), until uploads exist. */
export const PHOTO_BG = 'radial-gradient(120% 70% at 75% 20%, rgba(255,214,160,.55), transparent 55%), radial-gradient(60% 50% at 20% 85%, rgba(120,90,60,.7), transparent 70%), linear-gradient(160deg, #8a7160 0%, #4a3b33 55%, #2b2320 100%)'

/** An uploaded image (POST /v1/app/media answers an https URL; the mock a data: URL), as opposed to a
    stand-in name from before uploads existed. */
export const isImageUrl = (v: string | null | undefined): v is string => !!v && /^(https:|data:image\/|blob:)/.test(v)
/** The welcome band's background: the merchant's photo, cover-fitted, or the stand-in gradient. */
export const photoBg = (photo: string | null | undefined) => (isImageUrl(photo) ? `center / cover no-repeat url("${photo.replace(/"/g, '%22')}")` : PHOTO_BG)

export interface WelcomeProps {
  layout: WelcomeLayout
  colour: string
  logo: boolean
  logoUrl?: string | null
  photo: string | null
  sentence: string
  promise: string
  brand: string
  place?: string
  design: WheelDesign
  /** « Rien — la demande seule »: the page promises nothing to win */
  noGift?: boolean
}

/** The merchant's welcome page, drawn at `width` px wide (a phone is 300). */
export default function WelcomeMini({ width = W, ...p }: WelcomeProps & { width?: number }) {
  const k = width / W
  return (
    <div style={{ width, height: H * k, overflow: 'hidden', borderRadius: 18 * k, border: `${Math.max(1, 2 * k)}px solid ${INK}`, background: PAPER, flex: 'none' }} data-welcome={p.layout}>
      <div style={{ width: W, height: H, transform: `scale(${k})`, transformOrigin: '0 0', position: 'relative', fontFamily: 'var(--bmr-font-body, "Plus Jakarta Sans", sans-serif)', color: INK }}>
        <Page {...p} />
      </div>
    </div>
  )
}

function Logo({ brand, colour, logo, logoUrl, size = 52 }: { brand: string; colour: string; logo: boolean; logoUrl?: string | null; size?: number }) {
  return (
    <div style={{ width: size, height: size, borderRadius: 12, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,.18)', display: 'grid', placeItems: 'center', flex: 'none' }}>
      {logo && isImageUrl(logoUrl)
        ? <img src={logoUrl} alt="" style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
        : logo
        ? <span style={{ font: `600 ${size * 0.46}px/1 Lora, Georgia, serif`, color: colour }}>{brand.slice(0, 1)}</span>
        : <span style={{ fontSize: 9, color: '#8a8f86', textAlign: 'center', lineHeight: 1.1 }}>sans<br />logo</span>}
    </div>
  )
}

function Page({ layout, colour, logo, logoUrl, photo, sentence, promise, brand, place, design, noGift }: WelcomeProps) {
  const btn = buttonOf(colour)
  const h1: CSSProperties = { font: '500 25px/1.2 Lora, Georgia, serif', margin: 0, letterSpacing: '-.01em' }
  const body = (
    <div style={{ padding: '0 22px', display: 'grid', gap: 10 }}>
      <h1 style={h1}>{sentence}</h1>
      <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.45, color: '#4a5048' }}>{promise}</p>
    </div>
  )
  const foot = (
    <div style={{ position: 'absolute', left: 22, right: 22, bottom: 22, display: 'grid', gap: 10 }}>
      <div style={{ background: btn, color: textOn(btn), borderRadius: 12, padding: '14px 0', textAlign: 'center', fontWeight: 700, fontSize: 15 }}>{noGift ? 'Commencer' : 'Découvrir mon cadeau'}</div>
      {!noGift && <p style={{ margin: 0, textAlign: 'center', fontSize: 11, color: '#6b7068' }}>Le cadeau reste disponible, avec ou sans avis.</p>}
    </div>
  )
  if (layout === 'photo' || layout === 'couleur') {
    const band = layout === 'photo'
      ? { background: photo ? photoBg(photo) : '#d9d6cd' }
      : { background: colour }
    const onBand = layout === 'photo' ? '#FFFFFF' : textOn(colour)
    return (<>
      <div style={{ height: H * 0.44, position: 'relative', overflow: 'hidden', ...band }}>
        {layout === 'photo' && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,.25) 0%, transparent 35%, rgba(20,18,16,.55) 100%)' }} />}
        {layout === 'couleur' && !noGift && <div style={{ position: 'absolute', right: -70, bottom: -80, opacity: 0.22 }}><Wheel design={{ ...design, a: onBand === '#FFFFFF' ? '#FFFFFF' : INK, b: colour }} size={240} /></div>}
        <div style={{ position: 'absolute', top: 14, left: 22, right: 22, display: 'flex', justifyContent: 'space-between', color: onBand, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
          <span>{brand}</span>{place && <span style={{ fontWeight: 500, letterSpacing: 0, textTransform: 'none', opacity: 0.85 }}>{place}</span>}
        </div>
      </div>
      <div style={{ position: 'relative', marginTop: -26, padding: '0 22px 14px' }}><Logo brand={brand} colour={colour} logo={logo} logoUrl={logoUrl} /></div>
      {body}
      {foot}
    </>)
  }
  /* Simple: paper only — logo and name, the sentence, a small wheel teaser (none with no gift) */
  return (<>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '22px 22px 18px' }}>
      <Logo brand={brand} colour={colour} logo={logo} logoUrl={logoUrl} size={36} />
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>{brand}</span>
      {place && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7068' }}>{place}</span>}
    </div>
    {body}
    {!noGift && <div style={{ display: 'grid', placeItems: 'center', marginTop: 26 }}><Wheel design={{ ...design, a: colour, logoUrl }} size={130} /></div>}
    {foot}
  </>)
}
